import React, { useEffect, useState, useRef } from 'react';
import { Download, Table, List, Loader2 } from 'lucide-react';
import { Toast, ToastProps } from './Toast';
import { Button } from './SubscriptionAppButton';
interface ExportButtonProps {
  onExport?: (type: string) => Promise<void>;
  className?: string;
  variant?: 'orders' | 'contacts' | 'subscriptions' | 'tasks';
}
export function ExportButton({
  onExport,
  className = '',
  variant = 'orders'
}: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeExport, setActiveExport] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleExport = async (type: string, label: string) => {
    setActiveExport(type);
    // Simulate export delay (1.5s)
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (onExport) {
      await onExport(type);
    }
    setActiveExport(null);
    setIsOpen(false);
    // Show success toast
    const newToast: ToastProps = {
      id: Date.now().toString(),
      type: 'success',
      title: 'Export complete',
      message: `${label} has been downloaded successfully.`,
      onClose: removeToast
    };
    setToasts(prev => [...prev, newToast]);
  };
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  return <div className="relative" ref={dropdownRef} data-id="element-3040" data-mp="2jcid8z19w9wm">
      {/* Trigger Button */}
      <Button variant="outline" size="md" onClick={() => setIsOpen(!isOpen)} iconLeft={<Download className="w-4 h-4" data-id="element-3042" data-mp="2ncki1h2b1jg4" />} className={isOpen ? 'bg-[#F8FAFC] ring-2 ring-[#E0F2FE] border-[#0EA5E9]' : ''} data-id="element-3041" data-mp="2jafpklnlesv2">
        Export
      </Button>

      {/* Dropdown Menu */}
      {isOpen && <div className="absolute top-full right-0 mt-2 w-[280px] bg-white border border-[#E2E8F0] rounded-lg shadow-[0_4px_12px_rgba(15,23,42,0.1)] z-[1000] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-3043" data-mp="3lp52uztozque">
          {variant === 'orders' && <>
              {/* Orders Page Menu */}
              <div className="py-2" data-id="element-3044" data-mp="37qoxbc7kg6du">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-3046" data-mp="32rlsnsvi026e" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-3045" data-mp="32r2e8m523vx4" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-3048" data-mp="3cnovl0y6c4ue" />} label="Export All Orders (CSV)" onClick={() => handleExport('all_orders', 'All orders list')} isLoading={activeExport === 'all_orders'} disabled={activeExport !== null} data-id="element-3047" data-mp="3co6xoe7ipda8" />
              </div>
            </>}

          {variant === 'contacts' && <>
              {/* Contacts Page Menu */}
              <div className="py-2" data-id="element-3049" data-mp="zkwive3ll04f">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-3051" data-mp="14iemcoyuin51" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-3050" data-mp="14hwqhtg3iaxn" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-3053" data-mp="ula95emza20d" />} label="Export All Contacts (CSV)" onClick={() => handleExport('all_contacts', 'All contacts list')} isLoading={activeExport === 'all_contacts'} disabled={activeExport !== null} data-id="element-3052" data-mp="ujpcnjh3s8zv" />
              </div>
            </>}

          {variant === 'subscriptions' && <>
              {/* Subscriptions/Activity Log Page Menu */}
              <div className="py-2" data-id="element-3054" data-mp="1dh096wbcoobm">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-3056" data-mp="2185rrj8odupm" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-3055" data-mp="1dfyp62ccwrhr" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-3058" data-mp="ib2ebfbcbv7j" />} label="Export All Events (CSV)" onClick={() => handleExport('all_events', 'All events list')} isLoading={activeExport === 'all_events'} disabled={activeExport !== null} data-id="element-3057" data-mp="iakk0o88nr29" />
              </div>
            </>}

          {variant === 'tasks' && <>
              {/* Tasks Page Menu */}
              <div className="py-2" data-id="element-3059" data-mp="3f4muacd3f1n">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-3061" data-mp="1111zhr428q5o" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-3060" data-mp="111bq9gwxj1cf" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-3063" data-mp="r961eg1z501g" />} label="Export All Tasks (CSV)" onClick={() => handleExport('all_tasks', 'All tasks list')} isLoading={activeExport === 'all_tasks'} disabled={activeExport !== null} data-id="element-3062" data-mp="r9o1xovo60f6" />
              </div>
            </>}
        </div>}

      {/* Toasts */}
      {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} data-id="element-3064" data-mp="1ez8qcnaldpvu" />)}
    </div>;
}
interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  subtext?: string;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}
function MenuItem({
  icon,
  label,
  subtext,
  onClick,
  isLoading,
  disabled
}: MenuItemProps) {
  return <button onClick={onClick} disabled={disabled} className={`
        w-full text-left px-4 py-3 flex items-start gap-3
        transition-colors duration-150 group
        ${disabled && !isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#F8FAFC] cursor-pointer'}
      `} data-id="element-3065" data-mp="218p4xuk3hb3n">
      <div className={`
        flex-shrink-0 mt-0.5 transition-colors
        ${isLoading ? 'text-[#0EA5E9]' : 'text-[#94A3B8] group-hover:text-[#0EA5E9]'}
      `} data-id="element-3066" data-mp="2f0di8162y5m7">
        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-3067" data-mp="2k1kotwwhkyra" /> : icon}
      </div>
      <div data-id="element-3068" data-mp="2ozuk07i7nzcw">
        <div className={`
          text-[14px] font-medium leading-tight
          ${isLoading ? 'text-[#0EA5E9]' : 'text-[#0F172A]'}
        `} data-id="element-3069" data-mp="2p0e5fy72l5vo">
          {isLoading ? 'Generating...' : label}
        </div>
        {subtext && !isLoading && <div className="text-[12px] text-[#94A3B8] mt-1 leading-tight" data-id="element-3070" data-mp="1zafkfoq21bbh">
            {subtext}
          </div>}
      </div>
    </button>;
}